import { DiaFestivo } from '@schemas/festivos.schema';
import { SaldoVacaciones } from '@schemas/vacaciones.schema';

/**
 * Convierte un Date a 'YYYY-MM-DD' en hora local (sin desfase UTC).
 */
export function toISODateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parsea 'YYYY-MM-DD' (mock viejo) o 'YYYY-MM-DDTHH:mm:ss[.fff]' (timestamp
 * ISO del backend real) como fecha local — evita el desfase UTC de
 * `new Date(str)` cuando el string trae timezone Z.
 *
 * Antes este helper hacía `dateStr.split('-')` directamente, lo que con el
 * formato ISO daba `[2026, 5, NaN]` y producía Invalid Date — eso ocultaba
 * los rangos en el calendario de FullCalendar.
 */
export function parseLocalDate(dateStr: string): Date {
  // Toma solo la parte de fecha (antes de la 'T' si la hay).
  const [year, month, day] = dateStr.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Misma fecha a las 00:00 locales.
 *
 * Necesario al comparar una fecha "de reloj" contra rangos: `parseLocalDate`
 * devuelve medianoche, así que un `new Date()` con la hora actual queda POR
 * ENCIMA del fin del rango y una ausencia que termina hoy se pierde en el
 * `sel <= fin`.
 */
export function inicioDelDia(fecha: Date): Date {
  const d = new Date(fecha);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Calcula días hábiles entre dos fechas (inclusive), excluyendo:
 *  - Sábados y domingos
 *  - Días festivos del array proporcionado
 *
 * @param inicio   Fecha de inicio (inclusiva)
 * @param fin      Fecha de fin (inclusiva)
 * @param festivos Lista de festivos a excluir (por defecto vacía → solo excluye fines de semana)
 */
export function calcularDiasHabiles(inicio: Date, fin: Date, festivos: DiaFestivo[] = []): number {
  const festivosSet = new Set(festivos.map((f) => f.fecha));
  let dias = 0;
  const actual = new Date(inicio);
  while (actual <= fin) {
    const dow = actual.getDay();
    if (dow !== 0 && dow !== 6 && !festivosSet.has(toISODateString(actual))) {
      dias++;
    }
    actual.setDate(actual.getDate() + 1);
  }
  return dias;
}

/**
 * Devuelve true si la fecha cae en algún festivo de la lista.
 */
export function esFestivo(fecha: Date, festivos: DiaFestivo[]): boolean {
  const str = toISODateString(fecha);
  return festivos.some((f) => f.fecha === str);
}

/**
 * Devuelve el nombre del festivo si la fecha coincide, undefined si no.
 */
export function getNombreFestivo(fecha: Date, festivos: DiaFestivo[]): string | undefined {
  const str = toISODateString(fecha);
  return festivos.find((f) => f.fecha === str)?.nombre;
}

/** Reparto FIFO de los días de un rango entre los ciclos disponibles. */
export interface DistribucionFIFO {
  /** Días que salen de un ciclo anterior al actual (el que vence primero). */
  delAnterior: number;
  /** Días que salen del ciclo en curso. */
  delActual: number;
  /** Días que salen de un ciclo que todavía no empieza (vacaciones tras el próximo aniversario). */
  delSiguiente: number;
  /**
   * Días SIN saldo ni a su propia fecha. Mayor que cero = la solicitud excede
   * el saldo y el backend la rechaza (422): se bloquea el envío.
   */
  excedente: number;
}

/** Días LFT por año de antigüedad (Art. 76 reformado 2023). Espejo de `CalcularDiasLFT` del backend. */
export function diasLFT(anio: number): number {
  if (anio <= 0) return 0;
  if (anio <= 5) return 10 + anio * 2;
  return 22 + Math.floor((anio - 6) / 5) * 2;
}

const MS_DIA = 1000 * 60 * 60 * 24;

interface CicloFIFO {
  numero: number;
  inicio: Date;
  aniversario: Date;
  vencimiento: Date;
  diasCorrespondientes: number;
  usados: number;
  enCurso: boolean;
}

/** Espejo de `GeneradoAFecha` del backend: proporcional antes del aniversario, completo después. */
function generadoAFecha(c: CicloFIFO, d: Date): number {
  if (d < c.inicio) return 0;
  if (d >= c.aniversario) return c.diasCorrespondientes;
  const meses = (d.getTime() - c.inicio.getTime()) / MS_DIA / 30.44;
  return Math.round(((c.diasCorrespondientes / 12) * meses) * 10) / 10;
}

function sumarAnios(d: Date, n: number): Date {
  return new Date(d.getFullYear() + n, d.getMonth(), d.getDate());
}

function sumarMeses(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, d.getDate());
}

/**
 * Reparte los días de un rango entre los ciclos del saldo con el MISMO FIFO
 * que valida el backend (`AsignarFifo`): cada día va al ciclo más antiguo que
 * esté vigente a esa fecha y tenga capacidad A ESA FECHA —no a hoy—, así que
 * unas vacaciones a futuro cuentan con los días que se habrán generado para
 * entonces. Si el rango pasa del próximo aniversario, se arma el ciclo que
 * empieza ese día.
 *
 * Vive aquí y no en un modal porque lo consumen el alta y la edición: el
 * empleado necesita ver el mismo impacto cuando pide días y cuando cambia las
 * fechas de una solicitud existente.
 *
 * Parte de los `diasUsados` del saldo, que ya traen lo comprometido. El
 * backend, en cambio, reordena lo comprometido y lo nuevo por fecha; los dos
 * coinciden salvo que lo nuevo caiga ANTES de días ya comprometidos y la
 * capacidad esté justa. Ahí el backend es la autoridad y su 422 se muestra
 * tal cual.
 */
export function calcularDistribucionFIFO(
  saldo: SaldoVacaciones | null,
  rango: Date[] | null,
  festivos: DiaFestivo[],
): DistribucionFIFO | null {
  if (!saldo || !rango || rango.length !== 2) return null;

  const [inicio, fin] = rango;
  const festivosSet = new Set(festivos.map((f) => f.fecha));
  const dias: Date[] = [];
  for (const d = inicioDelDia(inicio); d <= fin; d.setDate(d.getDate() + 1)) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6 && !festivosSet.has(toISODateString(d))) dias.push(new Date(d));
  }
  if (dias.length === 0) return null;

  const ciclos: CicloFIFO[] = [...saldo.periodos]
    .sort((a, b) => a.numero - b.numero)
    .map((p) => ({
      numero: p.numero,
      inicio: parseLocalDate(p.fechaInicio),
      aniversario: parseLocalDate(p.fechaAniversario),
      vencimiento: parseLocalDate(p.fechaVencimiento),
      diasCorrespondientes: p.diasCorrespondientes,
      usados: p.diasUsados,
      enCurso: p.enCurso,
    }));
  if (ciclos.length === 0) return null;

  // Ciclos que todavía no empiezan, hasta el que contiene el último día.
  const ultimo = dias[dias.length - 1];
  for (let c = ciclos[ciclos.length - 1]; c.aniversario <= ultimo; c = ciclos[ciclos.length - 1]) {
    const aniversario = sumarAnios(c.aniversario, 1);
    ciclos.push({
      numero: c.numero + 1,
      inicio: c.aniversario,
      aniversario,
      vencimiento: sumarMeses(aniversario, 6),
      diasCorrespondientes: diasLFT(c.numero + 1),
      usados: 0,
      enCurso: false,
    });
  }
  const numeroActual = ciclos.find((c) => c.enCurso)?.numero ?? ciclos[ciclos.length - 1].numero;

  const r: DistribucionFIFO = { delAnterior: 0, delActual: 0, delSiguiente: 0, excedente: 0 };
  for (const d of dias) {
    const vigentes = ciclos.filter((c) => c.inicio <= d && c.vencimiento >= d);
    const destino = vigentes.find((c) => c.usados < generadoAFecha(c, d));
    if (!destino) {
      // Igual que el backend: el día va al vigente más nuevo y consume lo que
      // éste genere después, así que también encarece los días siguientes.
      r.excedente++;
      const nuevo = vigentes[vigentes.length - 1];
      if (nuevo) nuevo.usados++;
      continue;
    }
    destino.usados++;
    if (destino.numero < numeroActual) r.delAnterior++;
    else if (destino.numero === numeroActual) r.delActual++;
    else r.delSiguiente++;
  }
  return r;
}

/**
 * Cómo leer el saldo en pantalla.
 *
 * `diasDisponiblesTotal` es A HOY y queda negativo con cualquier solicitud a
 * futuro válida (cuenta con días que aún no se generan). Eso NO es deuda: la
 * deuda real la marca el backend en `tieneSaldoNegativo` / `diasNegativo`.
 *
 * - `deuda`: hay días que no se cubrirán ni con lo que se genere → negativo.
 * - `comprometido`: el total de hoy es negativo pero todo se cubre a su fecha
 *   → "0 disponibles hoy · N comprometidos a futuro".
 * - `disponible`: lo normal.
 */
export type LecturaSaldo =
  | { tipo: 'deuda'; dias: number }
  | { tipo: 'comprometido'; disponiblesHoy: 0; aFuturo: number }
  | { tipo: 'disponible'; dias: number };

export function leerSaldo(s: {
  diasDisponiblesTotal: number;
  tieneSaldoNegativo: boolean;
  diasNegativo?: number;
}): LecturaSaldo {
  if (s.tieneSaldoNegativo) {
    return { tipo: 'deuda', dias: s.diasNegativo ?? Math.abs(s.diasDisponiblesTotal) };
  }
  if (s.diasDisponiblesTotal < 0) {
    return { tipo: 'comprometido', disponiblesHoy: 0, aFuturo: -s.diasDisponiblesTotal };
  }
  return { tipo: 'disponible', dias: s.diasDisponiblesTotal };
}

/**
 * Permite confirmar un rango de fechas escrito manualmente en un nz-range-picker.
 *
 * Comportamiento:
 *  - Si el usuario está en el primer input y la fecha es válida → mueve el foco al segundo.
 *  - Si ambos inputs tienen fechas válidas y el rango es coherente → lo confirma y cierra.
 *  - En cualquier otro caso → simplemente quita el foco del input activo.
 *
 * @param applyFn Función que recibe el rango resultante (llamar a `signal.set`)
 * @param event   Evento keydown.enter capturado en el elemento nz-range-picker
 */
export function commitRangeOnEnter(applyFn: (dates: Date[] | null) => void, event: Event): void {
  if (event instanceof KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  const host = event.currentTarget as HTMLElement;
  const inputs = Array.from(host.querySelectorAll('input')) as HTMLInputElement[];
  if (inputs.length < 2) return;

  const activeInput = event.target as HTMLInputElement;
  const isFirst = inputs.indexOf(activeInput) === 0;

  const parseYMD = (raw: string): Date | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw.trim());
    if (!m) return null;
    const [, y, mo, d] = [0, ...m.slice(1).map(Number)] as number[];
    const date = new Date(y, mo - 1, d);
    return date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d
      ? date
      : null;
  };

  const start = parseYMD(inputs[0].value);
  const end = parseYMD(inputs[1].value);

  if (start && end && start <= end) {
    applyFn([start, end]);
    inputs.forEach((i) => i.blur());
  } else if (isFirst && start) {
    inputs[1].focus();
    inputs[1].select();
  } else {
    activeInput.blur();
  }
}
